// The page's fold: one `RunProjection` per run, fed by `openRunStream` (#159).
//
// `applyEvent` is the reducer and `useReducer` is exactly its shape, so the
// component holds no state of its own about rows: the stream's `onRow` is
// `dispatch`, and a re-render is a fold. The `EventSource` lives as long as
// the run id does — opened in an effect, closed in its cleanup — so a page
// that moves to another run closes the first stream before it opens the
// second, and React's strict-mode double-invoke opens and closes one extra
// stream in development and nothing in production.

import { useEffect, useReducer, useState } from "react";

import type { LogRow } from "./contracts/stream";
import {
  applyEvent,
  createInitialProjectionState,
  type RunProjection,
} from "./features/ops/investigationProjection";
import { openRunStream, type EventSourceFactory } from "./stream/runStream";

export interface RunView {
  projection: RunProjection;
  /** The stream's resume cursor: what a reconnect asks for. */
  cursor: number;
  /** The `EventSource` reported an error and is reconnecting on its own. */
  reconnecting: boolean;
}

export function useRunProjection(
  runId: string,
  options: { EventSource?: EventSourceFactory; baseUrl?: string } = {},
): RunView {
  const [projection, dispatch] = useReducer(applyEvent, undefined, createInitialProjectionState);
  const [cursor, setCursor] = useState(0);
  const [reconnecting, setReconnecting] = useState(false);

  useEffect(() => {
    const stream = openRunStream(
      runId,
      {
        onRow: (row: LogRow) => {
          dispatch(row);
          setReconnecting(false);
        },
        onSeq: setCursor,
        onConnected: () => setReconnecting(false),
        onError: () => setReconnecting(true),
      },
      options,
    );
    return () => stream.close();
    // The stream is a function of the run id alone; the factory and base URL
    // are the page's constants.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  return { projection, cursor, reconnecting };
}
